<template>
  <div
    class="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
  >
    <div class="flex items-start justify-between gap-3">
      <!-- Left: File info and progress -->
      <div class="flex-1 min-w-0">
        <!-- File name -->
        <div class="flex items-center gap-2 mb-1">
          <svg
            class="w-4 h-4 text-gray-400 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p
            class="text-xs font-medium text-gray-900 truncate"
            :title="upload.fileName"
          >
            {{ upload.fileName }}
          </p>
        </div>

        <!-- Import type -->
        <p class="text-xs text-gray-500 mb-2">
          {{ getImportTypeLabel(upload.importType) }}
        </p>

        <!-- Progress bar -->
        <div v-if="status" class="space-y-1">
          <div class="w-full bg-gray-200 rounded-full h-1.5">
            <div
              class="h-1.5 rounded-full transition-all duration-300"
              :class="getProgressBarColor(status.status)"
              :style="{ width: `${status.progressPercentage}%` }"
            ></div>
          </div>

          <!-- Status text -->
          <div class="flex items-center justify-between text-xs">
            <span class="text-gray-600">
              {{ status.processedRecords }} / {{ status.totalRecords }}
              <span
                v-if="
                  status.status === 'completed' && status.failedRecords === 0
                "
                class="text-green-600 ml-1"
                >✓</span
              >
              <span
                v-if="status.status === 'completed' && status.failedRecords > 0"
                class="text-yellow-600 ml-1"
                >⚠</span
              >
              <span v-if="status.status === 'failed'" class="text-red-600 ml-1"
                >✗</span
              >
            </span>
            <span class="text-gray-500"
              >{{ status.progressPercentage.toFixed(0) }}%</span
            >
          </div>

          <!-- Show failed records if any -->
          <div
            v-if="status.failedRecords > 0"
            class="text-xs text-yellow-600 mt-1"
          >
            {{ status.failedRecords }} record(s) failed
          </div>
        </div>

        <!-- Loading state when status is not yet available -->
        <div v-else class="space-y-1">
          <div class="w-full bg-gray-200 rounded-full h-1.5">
            <div
              class="h-1.5 rounded-full bg-blue-500 animate-pulse w-1/4"
            ></div>
          </div>
          <p class="text-xs text-gray-500">Starting upload...</p>
        </div>
      </div>

      <!-- Right: Close button -->
      <button
        v-if="status?.status === 'completed' || status?.status === 'failed'"
        @click="handleRemove"
        class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
      >
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>

    <!-- Error message -->
    <div v-if="status?.errorMessage" class="mt-2 text-xs text-red-600">
      {{ status.errorMessage }}
    </div>

    <!-- Download error CSV link -->
    <a
      v-if="status?.errorCsvUrl"
      :href="status.errorCsvUrl"
      target="_blank"
      class="mt-2 text-xs text-primary-purple hover:underline inline-flex items-center gap-1"
    >
      Download error report ↓
    </a>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, watch, PropType } from "vue";
import { useBulkUpload } from "@/composables/useBulkUpload";
import { useBulkUploadStore } from "@/stores/bulkUploadStore";
import { getImportTypeLabel } from "@/constants/bulk-upload-configs";
import type { LocalStorageUpload } from "@/types/bulk-upload-types";

export default defineComponent({
  name: "BulkUploadStatusItem",
  props: {
    uploadId: {
      type: String,
      required: true,
    },
    upload: {
      type: Object as PropType<LocalStorageUpload>,
      required: true,
    },
  },
  setup(props) {
    const store = useBulkUploadStore();
    const { fetchStatus } = useBulkUpload();

    // Fetch status with polling
    const { result: statusResult, stop: stopPolling } = fetchStatus(
      props.uploadId
    );

    const status = computed(() => {
      const data = statusResult.value?.bulkUploadStatus;
      if (data) {
        store.updateStatus(props.uploadId, data);
        return data;
      }
      return store.uploadStatuses.get(props.uploadId);
    });

    // Stop polling when completed or failed
    watch(
      () => status.value?.status,
      (newStatus) => {
        if (newStatus === "completed" || newStatus === "failed") {
          stopPolling();
        }
      }
    );

    const getProgressBarColor = (status: string) => {
      switch (status) {
        case "completed":
          return "bg-green-500";
        case "failed":
          return "bg-red-500";
        case "processing":
          return "bg-primary-purple";
        default:
          return "bg-blue-500";
      }
    };

    const handleRemove = () => {
      store.removeUpload(props.uploadId);
    };

    return {
      status,
      getImportTypeLabel,
      getProgressBarColor,
      handleRemove,
    };
  },
});
</script>
